import { authenticatedFetch } from "./apiClient";

/**
 * Mirrors core's `OrgRole`, ordered: owner > admin > editor > viewer.
 *
 * `owner` holds every admin capability — what it adds is that it cannot be
 * removed or demoted, only transferred, which is what keeps an organization from
 * becoming unadministrable. Exactly one member holds it.
 *
 * `editor` is everything a solo user could do before the org migration:
 * generation, audits, page and policy edits, connecting integrations. What it
 * lacks is member management and domain CRUD, both of which stay `admin`.
 */
export type OrgRole = "owner" | "admin" | "editor" | "viewer";

/** The roles an admin may assign. Ownership moves only through `transferOwnership`. */
export const ASSIGNABLE_ROLES = ["admin", "editor", "viewer"] as const satisfies readonly OrgRole[];

export interface OrganizationOut {
  id: string;
  name: string;
  created_at: string;
  role: OrgRole;
}

/**
 * A membership as the UI consumes it. Still core's `MemberOut` shape, but the
 * roster is read live from Firestore (`orgSlice.subscribeToMembers`) — the
 * mutating endpoints below return it too.
 */
export interface MemberOut {
  uid: string;
  role: OrgRole;
  joined_at: string;
  // Snapshot of the member's user profile, stored on the member document at
  // join time. Both are null when the profile carried neither, and for
  // memberships written before the fields existed.
  email: string | null;
  display_name: string | null;
  invited_by: string | null;
}

/** Mirrors core's `ActionType`; see `src/schemas/firestore/userActionsLedger.schema.ts`. */
export type LedgerActionName = string;

/**
 * A ledger entry as the UI consumes it. No endpoint returns this any more —
 * `ActivityFeed` subscribes to the collection directly — but the shape stays
 * core's `ActionOut` so the feed and core's API describe the same thing.
 */
export interface ActionOut {
  at: string;
  /** Null on `system` entries — a scheduled run has no member behind it. */
  actor_uid: string | null;
  actor_type: "user" | "agent" | "system" | "external_agent";
  /** The member who asked, when a machine did the work. Null otherwise. */
  on_behalf_of: string | null;
  action: LedgerActionName;
  target_path: string;
  domain_id: string | null;
  summary: string | null;
  /**
   * The conversation turn behind an assistant-driven entry — "why", where
   * `actor_type` gives "who". Null on hand-clicked and scheduled entries, and
   * on anything recorded before these fields existed.
   */
  source_thread_id: string | null;
  source_message_id: string | null;
}

/**
 * Thrown when the caller's email is not on core's org-creation allowlist.
 *
 * Its own type because the UI does something other than show an error: it
 * replaces the create form with a "get invited instead" screen. Recognized by
 * the `code` on the 403 body, never by the message — the wording is copy and
 * will change.
 */
export class OrgCreationNotAllowedError extends Error {
  constructor(message?: string) {
    super(message || "This account is not allowed to create an organization.");
    this.name = "OrgCreationNotAllowedError";
  }
}

/**
 * FastAPI's `detail` is a plain string on most routes and an object on the ones
 * whose failure the UI branches on. Unwrap both here rather than at each call
 * site — a raw object reaches the user as "[object Object]".
 */
function detailMessage(detail: unknown): string | null {
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "message" in detail) {
    const { message } = detail as { message?: unknown };
    if (typeof message === "string") return message;
  }
  return null;
}

function detailCode(body: unknown): string | null {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (detail && typeof detail === "object" && "code" in detail) {
    const { code } = detail as { code?: unknown };
    if (typeof code === "string") return code;
  }
  return null;
}

export async function parseOrThrow<T>(response: Response, action: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      detailMessage(body?.detail) || `${action} failed: ${response.statusText}`,
    );
  }
  return response.json();
}

export class OrganizationService {
  /** The caller's organization, or null if they don't belong to one yet. */
  static async getMyOrganization(): Promise<OrganizationOut | null> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/organizations/me`,
    );
    if (response.status === 404) {
      return null;
    }
    return parseOrThrow<OrganizationOut>(response, "Fetching organization");
  }

  /**
   * Whether this account may create an organization, asked before the form is
   * rendered. Failing open on a network error is deliberate: the POST is the
   * real gate, so the worst case is the old behaviour — the form appears and
   * the submit is refused.
   */
  static async isOrgCreationAllowed(): Promise<boolean> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/organizations/creation-eligibility`,
    );
    if (!response.ok) return true;
    const body = await response.json().catch(() => null);
    return body?.is_allowed !== false;
  }

  static async createOrganization(name: string): Promise<OrganizationOut> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/organizations`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      },
    );
    if (response.status === 403) {
      const body = await response.clone().json().catch(() => null);
      if (detailCode(body) === "org_creation_not_allowed") {
        throw new OrgCreationNotAllowedError(
          detailMessage(body?.detail) ?? undefined,
        );
      }
    }
    return parseOrThrow<OrganizationOut>(response, "Creating organization");
  }

  static async addMember(
    orgId: string,
    uidOrEmail: string,
    role: OrgRole = "viewer",
  ): Promise<MemberOut> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/organizations/${orgId}/members`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid_or_email: uidOrEmail, role }),
      },
    );
    return parseOrThrow<MemberOut>(response, "Adding member");
  }

  static async updateMemberRole(
    orgId: string,
    uid: string,
    role: OrgRole,
  ): Promise<MemberOut> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/organizations/${orgId}/members/${uid}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      },
    );
    return parseOrThrow<MemberOut>(response, "Updating member role");
  }

  /** Hand the org to another member; the caller becomes an admin. Owner only. */
  static async transferOwnership(orgId: string, uid: string): Promise<MemberOut> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/organizations/${orgId}/members/${uid}/transfer-ownership`,
      { method: "POST" },
    );
    return parseOrThrow<MemberOut>(response, "Transferring ownership");
  }

  static async removeMember(orgId: string, uid: string): Promise<void> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/organizations/${orgId}/members/${uid}`,
      { method: "DELETE" },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(
        detailMessage(body?.detail) || `Removing member failed: ${response.statusText}`,
      );
    }
  }
}
